import { TiChartSeriesComponent } from "../components/ti-chart-series.component";
import { DateHelpers } from "../helpers/date-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { DrawEarningNotificationDataModel } from "../models/earning/draw-earning-notification-data.model";
import { Timeframe } from "../models/enum/timeframe.enum";
import { NiceScale } from "../models/scale/nice-scale";

export class HighChartAxisBuilder {
    private id = StringHelpers.newGuid();
    public redrawYAxis = false;
    public manualMoveYAxisScale = false;
    public manualExpandYAxisScale = false;
    public xValuesForDateTime: Array<number> = [];
    public xValuesForDateTimeWithYear: Array<number> = [];
    public xValuesForHours: Array<number> = [];
    public xValuesForDarkersGridlines: Array<number> = [];

    private drawEarningNotificationModel: DrawEarningNotificationDataModel | null = null;
    private drawEarningNotificationElement: HTMLElement | null = null;

    private tiChartSeries: TiChartSeriesComponent;
    private extreamChangeTimer: any;

    constructor(tiChartSeries: TiChartSeriesComponent) {
        this.tiChartSeries = tiChartSeries;
        this.subscribeDrawEarningNotificationData();
        this.tiChartSeries.subscribeExtremeChange(this.id, () => {
            clearTimeout(this.extreamChangeTimer);
            this.extreamChangeTimer = setTimeout(() => {
                this.buildAxisComponents();
            }, 250);
        });
        this.tiChartSeries.subscribeRedrawChange(this.id, () => {
            setTimeout(() => {
                this.drawEarningImg();
            }, 50);
        });
    }

    public reset() {
        this.xValuesForDateTime = [];
        this.xValuesForDateTimeWithYear = [];
        this.xValuesForHours = [];
        this.xValuesForDarkersGridlines = [];

        this.drawEarningNotificationModel = null;
        this.drawEarningNotificationElement?.remove();
        this.drawEarningNotificationElement = null;
        
        this.manualMoveYAxisScale = false;
        this.manualExpandYAxisScale = false;
        this.redrawYAxis = false;
    }

    private drawTimer: any = 0;
    public buildAxisComponents(onlyYAxisWithTimer?: boolean) {
        if (!this.tiChartSeries.highStockChart?.xAxis?.length || !this.tiChartSeries.highStockChart?.yAxis?.length) {
            return;
        }

        let redraw = false;
        const xAxis = this.tiChartSeries.highStockChart.xAxis[0];
        const yAxis: any = this.tiChartSeries.highStockChart.yAxis[0];
        const highlight = () => {
            if (xAxis?.ticks && !this.tiChartSeries.base.marketingWebChartMode) {
                for (const t in xAxis.ticks) {
                    const tick = xAxis.ticks[t];
                    if (this.tiChartSeries.highChartAxisBuilder.xValuesForDarkersGridlines.some((x: number) => x == tick.pos)) {
                        tick.gridLine?.element?.classList.add('darker');
                        tick.label?.element?.classList.add('bold');
                    }
                }
            }
        };

        const yAxisUpdate = () => {
            if (yAxis) {
                const tickPositions = this.getYAxisLabelTickPositions();
                const shouldRedraw = JSON.stringify(tickPositions) != JSON.stringify(yAxis.tickPositions);
                if (shouldRedraw || this.redrawYAxis) {
                    if (this.manualExpandYAxisScale) {
                        yAxis.update({
                            startOnTick: false,
                            endOnTick: false,
                            tickPositions: null
                        }, true);
                    } else {
                        yAxis.update({
                            startOnTick: false,
                            endOnTick: false,
                            tickPositions: tickPositions
                        }, false);
                    }

                    redraw = true;
                    this.redrawYAxis = false;
                }
            }
        };

        if (onlyYAxisWithTimer) {
            yAxisUpdate();
            // if (redraw) {
            // this.tiChartSeries.highStockChart?.redraw(false);
            // clearTimeout(this.drawTimer)
            // this.drawTimer = setTimeout(() => {
            //     yAxisUpdate();
            //     highlight();
            //     if (redraw) {
            //         // this.tiChartSeries.highStockChart?.redraw(false);
            //     }
            // }, 20);
            // }
            return;
        }

        yAxisUpdate();

        this.xValuesForDateTime = [];
        this.xValuesForDateTimeWithYear = [];
        this.xValuesForHours = [];
        this.xValuesForDarkersGridlines = [];
        if (xAxis) {
            const tickPositions = this.getXAxisLabelTickPositions();
            const prePostMarketPlotBands = this.getXAxisPrePostMarketPlotBands();
            const earningNotificationPlotBands = this.getXAxisEarningNotificationPlotBands();
            const plotBands = prePostMarketPlotBands.concat(earningNotificationPlotBands);
            const existingPlotBands = this.getExistingPlotBands(xAxis.plotLinesAndBands)
            const shouldRedraw = JSON.stringify(plotBands) != JSON.stringify(existingPlotBands) || JSON.stringify(tickPositions) != JSON.stringify(xAxis.tickPositions);
            if (shouldRedraw) {
                xAxis.update({
                    tickPositions: tickPositions,
                    plotBands: plotBands
                }, false);

                redraw = true;
            }
        }

        if (redraw) {
            highlight();

            this.tiChartSeries.highStockChart?.redraw(false);
        }

        this.drawEarningImg();
    }

    private drawEarningImg() {
        const xAxis: any = this.tiChartSeries.highStockChart?.xAxis[0];

        if (!xAxis || !this.drawEarningNotificationModel || isNaN(parseFloat(this.drawEarningNotificationModel.xValue + '')) || this.drawEarningNotificationModel.xValue < xAxis.min || this.drawEarningNotificationModel.xValue > xAxis.max) {
            this.drawEarningNotificationElement?.remove();
            this.drawEarningNotificationElement = null;
            return;
        }

        if (TimeframeHelper.timeframeIntraday(this.tiChartSeries.currentTimeframe) || this.tiChartSeries.currentTimeframe > 21) {
            this.drawEarningNotificationElement?.remove();
            this.drawEarningNotificationElement = null;
            return;
        }

        let adding = false;

        const highchartContainer = this.tiChartSeries.element.findFirst('.highcharts-container');
        this.drawEarningNotificationElement = highchartContainer?.findFirst('.earning-img');
        if (!this.drawEarningNotificationElement) {
            adding = true;
            this.drawEarningNotificationElement = document.createElement('div');
        }

        const myWidth = 10;
        const extraTop = this.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0;
        const xAxisTop = xAxis.height + extraTop;

        let plotX = xAxis.toPixels(this.drawEarningNotificationModel.xValue);

        let top = 31 + xAxisTop;

        if (!this.tiChartSeries.base.marketingWebChartMode) {
            if (this.tiChartSeries.base.pnpMode) {
                if (this.tiChartSeries.chartStyleSettings?.pnpChangePercentEnabled) {
                    top -= 20;
                } else {
                    top = xAxisTop - 14;
                }
            } else {

                if (this.tiChartSeries.base.element.containClass('one')) {
                    top -= 15;
                }
            }
        }

        if (this.tiChartSeries.base.marketingWebChartMode) {
            if (!this.tiChartSeries.base.pnpMode) {
                top -= 10;

                if (!this.tiChartSeries.base.element.containClass('one')) {
                    top += 24;
                }
            } else {
                top -= 20;
            }
        }

        let left = plotX - myWidth / 2;

        if (top < 2) {
            top = 2;
        }

        if (left < 2) {
            left = 2;
        }

        this.drawEarningNotificationElement.className = 'earning-img';
        this.drawEarningNotificationElement.style.position = 'absolute';
        this.drawEarningNotificationElement.style.top = top + 'px';
        this.drawEarningNotificationElement.style.left = left + 'px';
        this.drawEarningNotificationElement.style.zIndex = '2';

        const tooltip = this.drawEarningNotificationModel.getTooltip();
        if (adding) {
            this.drawEarningNotificationElement.innerHTML = `<img src="${this.tiChartSeries.base.basePath}assetfiles/img/Earnings.png" title="${tooltip}" />`;
            highchartContainer?.insertAdjacentElement('beforeend', this.drawEarningNotificationElement);
        } else {
            this.drawEarningNotificationElement.findFirst('img')?.setAttribute('title', tooltip);
        }
    }

    private getYAxisLabelTickPositions(): Array<number> {
        const tickPositions: Array<number> = [];
        const yAxis: any = this.tiChartSeries.highStockChart.yAxis[0];
        if (!yAxis || this.manualExpandYAxisScale) {
            return tickPositions;
        }

        const dataMin = yAxis.min ?? 0;
        const dataMax = yAxis.max ?? 0;

        let max = dataMax;
        const min = dataMin;
        const niceScale = new NiceScale(min, max);
        niceScale.setMaxTicks(5);
        let interval = niceScale.getTickSpacing();
        max = niceScale.getNiceMax();
        if (interval < 0.001)
            interval = 0.001;

        let firstGridLine = niceScale.getNiceMin();
        if (firstGridLine == min)
            firstGridLine = niceScale.getNiceMin() + interval;

        if (this.tiChartSeries.base.pnpMode) {
            tickPositions.push(firstGridLine - interval);
        }

        for (let i = firstGridLine; i <= max; i += interval) {
            if (dataMin <= i && i <= dataMax) {
                tickPositions.push(i);
            }
        }

        if (this.tiChartSeries.base.pnpMode) {
            tickPositions.push(max + interval);
        } else {
            tickPositions.push(yAxis.max);
        }

        return tickPositions;
    }

    private getXAxisEarningNotificationPlotBands(): Array<any> {
        const result: Array<any> = [];

        if (this.drawEarningNotificationModel) {

            if (TimeframeHelper.timeframeIntraday(this.tiChartSeries.currentTimeframe) || this.tiChartSeries.currentTimeframe > 21) {
                return result;
            }

            const xValue = parseFloat(this.drawEarningNotificationModel.xValue + '');
            if (isNaN(xValue)) {
                return result;
            }

            result.push({
                className: 'earning-notification-plot-band',
                acrossPanes: false,
                from: xValue - 0.6,
                to: xValue + 0.6,
            });
        }

        return result;
    }

    private getExistingPlotBands(plotBands: Array<any>): Array<any> {
        const result: Array<any> = [];

        for (const p of plotBands) {
            if (!p.options) {
                continue;
            }


            result.push({
                className: p.options.acrossPanes,
                acrossPanes: false,
                from: p.options.from,
                to: p.options.to,
            });
        }

        return result;
    }

    private getXAxisPrePostMarketPlotBands(): Array<any> {
        const result: Array<any> = [];

        const showPreMarket = this.tiChartSeries.chartStyleSettings?.showPreMarket ?? false;
        const showPostMarket = this.tiChartSeries.chartStyleSettings?.showPostMarket ?? false;
        if (!this.tiChartSeries.highStockChart?.xAxis ||
            !this.tiChartSeries.highStockChart?.xAxis[0] ||
            !TimeframeHelper.timeframeIntraday(this.tiChartSeries.currentTimeframe) ||
            (!showPreMarket && !showPostMarket)) {
            return [];
        }

        let preFrom = 0;
        let postFrom = 0;
        let isPreMarket = false;
        let isPostMarket = false;
        for (let i = 0; i < this.tiChartSeries.tiChartPoints.length; i++) {
            const point = this.tiChartSeries.tiChartPoints[i];
            const isLast = i == this.tiChartSeries.tiChartPoints.length - 1;
            if (point.isPreMarket && !isPreMarket) {
                isPreMarket = true;
                preFrom = point.xValue;
            } else if (isPreMarket && (!point.isPreMarket || isLast)) {
                isPreMarket = false;
                result.push({
                    className: 'pre-market-plot-band',
                    acrossPanes: false,
                    from: preFrom,
                    to: point.xValue
                });
            }

            if (point.isPostMarket && !isPostMarket) {
                isPostMarket = true;
                postFrom = point.xValue;
            } else if (isPostMarket && (!point.isPostMarket || isLast)) {
                isPostMarket = false;
                result.push({
                    className: 'post-market-plot-band',
                    acrossPanes: false,
                    from: postFrom,
                    to: point.xValue
                });
            }
        }

        return result;
    }

    private getXAxisLabelTickPositions(): Array<number> {
        const result: Array<number> = [];
        // const temp: Array<any> = [];
        // const temp2: Array<any> = [];

        if (!this.tiChartSeries.highStockChart?.xAxis || !this.tiChartSeries.highStockChart?.xAxis[0]) {
            return [];
        }

        if (!TimeframeHelper.timeframeIntraday(this.tiChartSeries.currentTimeframe)) {
            let previousPoint: TIChartPointModel | null = null;
            let previousRightEdge = -9999999;
            for (const point of this.tiChartSeries.tiChartPoints) {
                let labelPoint: TIChartPointModel | null = null;
                if (previousPoint != null && DateHelpers.format('yyyy', previousPoint.start) != DateHelpers.format('yyyy', point.start)) {
                    labelPoint = point;
                    this.xValuesForDateTimeWithYear.push(labelPoint.xValue);
                } else if (this.tiChartSeries.currentTimeframe != Timeframe.Month && previousPoint != null && DateHelpers.format('yyyy-mm', previousPoint.start) != DateHelpers.format('yyyy-mm', point.start)) {
                    labelPoint = point;
                    this.xValuesForDateTime.push(labelPoint.xValue);
                }
                else if (this.tiChartSeries.base.pnpMode && this.tiChartSeries.currentTimeframe != Timeframe.Month && point.start.getDate() % 6 == 0) {
                    labelPoint = point;
                    this.xValuesForDateTimeWithYear.push(labelPoint.xValue);
                }

                const labelWidth = labelPoint ? 38 : 0;
                const labelWidthAsX = this.getXAxisDistance(labelWidth)
                // const customLabelBuffer = labelWidthAsX / 2;
                const customLabelBuffer = labelWidthAsX;
                const leftEdge = point.xValue - customLabelBuffer;
                const rightEdge = point.xValue + customLabelBuffer;

                if (labelPoint && leftEdge > previousRightEdge) {
                    result.push(labelPoint.xValue);
                    previousRightEdge = rightEdge;
                }

                previousPoint = point;
            }
        } else {
            const hoursInChart = this.getTotalHoursInChart();
            let showFiveMinuteLabels = false;
            let showQuarterHours = false;
            let showAllHours = false;
            let showNoonAndThreePm = true;
            if (hoursInChart < 2) {
                showFiveMinuteLabels = true;
                showQuarterHours = true;
                showAllHours = true;
            } else if (hoursInChart < 3) {
                showQuarterHours = true;
                showAllHours = true;
            } else if (hoursInChart < 8) {
                showAllHours = true;
            } else if (hoursInChart < 48) {
                showAllHours = true;
            } else if (hoursInChart < 96) {
                showNoonAndThreePm = true;
            }

            let previousPoint: TIChartPointModel | null = null;
            let previousRightEdge = -9999999;
            for (const point of this.tiChartSeries.tiChartPoints) {
                let labelPoint: TIChartPointModel | null = null;

                if (!point.start) {
                    continue;
                }

                let isHighlightGridline = false;
                if (previousPoint != null && DateHelpers.format('yyyy', previousPoint.start) != DateHelpers.format('yyyy', point.start)) {
                    labelPoint = point;
                    this.xValuesForDateTimeWithYear.push(labelPoint.xValue);
                    this.xValuesForDarkersGridlines.push(labelPoint.xValue);
                    isHighlightGridline = true;
                } else if (previousPoint != null && DateHelpers.format('yyyy-mm-dd', previousPoint.start) != DateHelpers.format('yyyy-mm-dd', point.start)) {
                    labelPoint = point;
                    this.xValuesForDateTime.push(labelPoint.xValue);
                    this.xValuesForDarkersGridlines.push(labelPoint.xValue);
                    isHighlightGridline = true;
                }

                // TODO make this smarter to show the next bar in the event there is not a 15 minute bar for example
                else if ((showAllHours && point.start.getMinutes() == 0)
                    || (showNoonAndThreePm && point.start.getMinutes() == 0 && (point.startMarketTimeZone.getHours() == 12 || point.startMarketTimeZone.getHours() == 15))
                    || (showQuarterHours && (point.start.getMinutes() == 15 || point.start.getMinutes() == 30 || point.start.getMinutes() == 45))
                    || (showFiveMinuteLabels && (point.start.getMinutes() % 5 == 0))
                ) {
                    labelPoint = point;
                    this.xValuesForHours.push(labelPoint.xValue);
                }

                const labelWidth = labelPoint ? 38 : 0;
                const labelWidthAsX = this.getXAxisDistance(labelWidth)
                const customLabelBuffer = labelWidthAsX / 2;
                const leftEdge = point.xValue - customLabelBuffer;
                const rightEdge = point.xValue + customLabelBuffer;

                if (labelPoint) {
                    if (leftEdge > previousRightEdge) {
                        result.push(labelPoint.xValue);
                        previousRightEdge = rightEdge;
                    } else if (result.length && isHighlightGridline && (previousRightEdge - leftEdge) > (customLabelBuffer / 2)) {
                        result[result.length - 1] = labelPoint.xValue;
                        previousRightEdge = rightEdge;
                    }
                }

                previousPoint = point;
            }
        }
        return result;
    }

    private getTotalHoursInChart(): number {
        let toReturn = 0;
        const mainSeries: any = this.tiChartSeries.highStockChart?.series[0];
        if (mainSeries) {
            const firstPointData = mainSeries.processedXData[0];
            const lastPointData = mainSeries.processedXData[mainSeries.processedXData.length - 1];
            const firstPoint = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == firstPointData);
            const lastPoint = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == lastPointData);
            if (firstPoint && firstPoint.start && lastPoint && lastPoint.start) {
                return DateHelpers.getHoursDifferent(firstPoint.start, lastPoint.start);
            }
        }
        return toReturn;
    }

    private getXAxisDistance(pixels: number) {
        if (!this.tiChartSeries.highStockChart?.xAxis[0]) {
            return 0;
        }

        const axis = this.tiChartSeries.highStockChart?.xAxis[0];
        if (!Number.isNaN(axis.min)) {
            const min = axis.min as number;
            const minPixel = axis.toPixels(min, true);
            const rightPixel = Math.abs(minPixel) + pixels;
            const rightValue = axis.toValue(rightPixel, true);
            return rightValue - min;
        }

        return 0;
    }

    private subscribeDrawEarningNotificationData() {
        this.tiChartSeries.chartService.subscribeDrawEarningNotificationData((model: DrawEarningNotificationDataModel) => {
            this.drawEarningNotificationModel = model;
            this.buildAxisComponents();
        });
    }
}